import type { IssuedCredential, LeadSourceAuthMode } from '../../lib/leadSourcesApi';

const MANAGE_ROLES = new Set(['owner', 'admin']);

interface BuildIntakeCredentialValueInput {
  apiBaseUrl: string;
  authMode: LeadSourceAuthMode;
  plaintext: string;
}

export type IntakeCredentialValue = { kind: 'url'; value: string } | { kind: 'key'; value: string };

// design §4.2: both auth modes present the same fls_<keyId>_<secret> token —
// mode A embeds it in the intake URL path, mode B presents it as the raw
// X-API-Key header value. This is the single place that distinction is made
// for display, so the secret modal never has to know about auth modes.
export function buildIntakeCredentialValue({
  apiBaseUrl,
  authMode,
  plaintext,
}: BuildIntakeCredentialValueInput): IntakeCredentialValue {
  if (authMode === 'header_key') {
    return { kind: 'key', value: plaintext };
  }
  const base = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;
  return { kind: 'url', value: `${base}/api/v1/lead-sources/intake/${plaintext}` };
}

// D9 client half: mutating controls (create/rotate/revoke/delete) are gated
// here, not in navigation.ts — the tab itself stays visible to every role.
export function canManageLeadSourceConnections(role: string | undefined): boolean {
  return !!role && MANAGE_ROLES.has(role);
}

// Creation happens on the list screen (tapping an "Available" provider), but
// the one-time secret can only be shown once — and the plaintext must never
// travel through a route param or navigation history. This in-memory handoff
// lets the detail screen consume it exactly once on the first mount after
// creation; a manual navigation to the same connection later finds nothing,
// which is the intended "no re-reveal" behavior (design §4.10).
const pendingSecrets = new Map<string, IssuedCredential>();

export function setPendingLeadSourceSecret(connectionId: string, credential: IssuedCredential) {
  pendingSecrets.set(connectionId, credential);
}

export function consumePendingLeadSourceSecret(connectionId: string): IssuedCredential | null {
  const credential = pendingSecrets.get(connectionId);
  if (!credential) return null;
  pendingSecrets.delete(connectionId);
  return credential;
}
